import { ApiError, request } from "./client";
import type { ContentBundle, Explainer, FastProfileQuestion, FastProfileQuestionOption } from "../types";

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const INPUT_KINDS = new Set(["single_select", "multi_select", "date", "date_or_unknown"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isSource(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (!nonEmptyString(value.publisher) || !nonEmptyString(value.title)) return false;
  if (!DATE.test(String(value.reviewed_at))) return false;
  if (!nonEmptyString(value.url)) return false;
  try {
    return new URL(value.url).protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * `key`, `label` and `plain_meaning` are required; every other field is
 * optional and, when present, must be a non-empty string (or, for
 * `sources`, a non-empty array of valid sources). This mirrors the server
 * contract exactly: a field is omitted rather than sent empty.
 */
function isExplainer(value: unknown): value is Explainer {
  if (!isRecord(value)) return false;
  if (!nonEmptyString(value.key) || !nonEmptyString(value.label) || !nonEmptyString(value.plain_meaning)) {
    return false;
  }
  for (const field of ["dutch_term", "dutch_gloss", "why_we_ask", "example", "where_to_find"] as const) {
    if (value[field] !== undefined && !nonEmptyString(value[field])) return false;
  }
  if (value.sources !== undefined) {
    if (!Array.isArray(value.sources) || value.sources.length === 0 || !value.sources.every(isSource)) {
      return false;
    }
  }
  return true;
}

function isOption(value: unknown): value is FastProfileQuestionOption {
  if (!isRecord(value)) return false;
  if (!nonEmptyString(value.value) || !nonEmptyString(value.label)) return false;
  if (value.description !== undefined && !nonEmptyString(value.description)) return false;
  if (value.explainer_keys !== undefined && !isStringArray(value.explainer_keys)) return false;
  return true;
}

function isQuestion(value: unknown): value is FastProfileQuestion {
  if (!isRecord(value)) return false;
  if (!nonEmptyString(value.field) || !nonEmptyString(value.legend)) return false;
  if (![1, 2, 3, 4, 5].includes(value.step as number)) return false;
  if (!INPUT_KINDS.has(String(value.input_kind))) return false;
  if (value.hint !== undefined && !nonEmptyString(value.hint)) return false;
  if (value.explainer_keys !== undefined && !isStringArray(value.explainer_keys)) return false;
  if (value.options !== undefined) {
    if (!Array.isArray(value.options) || !value.options.every(isOption)) return false;
  }
  return true;
}

export function isValidContentBundle(value: unknown): value is ContentBundle {
  if (!isRecord(value) || !nonEmptyString(value.content_version)) return false;
  if (!isRecord(value.explainers)) return false;
  for (const [key, explainer] of Object.entries(value.explainers)) {
    if (!isExplainer(explainer) || explainer.key !== key) return false;
  }
  if (!isRecord(value.fast_profile) || !Array.isArray(value.fast_profile.questions)) return false;
  if (!value.fast_profile.questions.every(isQuestion)) return false;
  return true;
}

/**
 * Fetch and validate `GET /api/v1/content`. The endpoint accepts no query
 * parameter and is identical for every caller — see
 * `docs/architecture/features/W4-01.md`. Any malformed payload is treated as
 * a fetch failure so the caller's existing bundle-unavailable handling
 * (`ContentProvider`) covers both cases with one code path.
 */
export async function fetchContentBundle(): Promise<ContentBundle> {
  const value = await request<unknown>("/content");
  if (!isValidContentBundle(value)) {
    throw new ApiError("We couldn’t load the help content right now.", {
      code: "invalid_content_bundle",
    });
  }
  return value;
}
